import { useCallback } from 'react';
import { useCustomTasks, useCompletedTasks } from './useLocalStorage';
import { useGameification } from './useGameification';
import { useRewardsSystem } from '../features/gamification';

/**
 * useTaskManager — Unified hook for task state and completion logic.
 *
 * Composes:
 *   - useCustomTasks       → task CRUD + Firestore/Calendar sync
 *   - useCompletedTasks    → per-day completion state
 *   - useGameification     → XP, streaks, badges
 *   - useRewardsSystem     → coins
 *
 * The hook handles all business logic for completing tasks but deliberately
 * does NOT trigger UI side effects (modals, toasts, animations).
 * Callers receive action descriptors from completeTask() and handle UI themselves.
 *
 * @param {object} opts
 * @param {number} opts.weekOffset     — current week offset (0 = current week)
 * @param {number} opts.currentDayIndex — selected day index (0=Mon … 6=Sun)
 */
export function useTaskManager({ weekOffset, currentDayIndex }) {
  // ─── Base hooks ──────────────────────────────────────────────────────────────
  const {
    tasks,
    addTask,
    deleteTask,
    updateTask,
    getTasksForDate,
    clearAllTasks,
    isSyncing: tasksSyncing,
    syncError: tasksSyncError,
  } = useCustomTasks();

  const {
    toggleTask,
    toggleSubTask,
    isCompleted,
    isSubTaskCompleted,
    getCompletedCount,
  } = useCompletedTasks();

  const gameification = useGameification();
  const rewards = useRewardsSystem({ totalXP: gameification.totalXP });

  // ─── Derived helpers ──────────────────────────────────────────────────────────

  /**
   * isTaskCompleted — Checks whether a task is considered completed for a given
   * day.  For tasks with instructions, uses a majority threshold so that partially
   * completed multi-step tasks don't count.
   */
  const isTaskCompleted = useCallback((dayIndex, item) => {
    if (!item.instructions || item.instructions.length === 0) {
      return isCompleted(weekOffset, dayIndex, item.id);
    }
    const checked = getCompletedCount(weekOffset, dayIndex, item.id, item.instructions.length);
    return checked > item.instructions.length / 2;
  }, [weekOffset, isCompleted, getCompletedCount]);

  // ─── Orchestrated completion ──────────────────────────────────────────────────

  /**
   * completeTask — Evaluates whether a task or subtask can be completed and
   * performs the state changes that don't require UI.
   *
   * Returns an action descriptor so the caller can handle UI side effects:
   *
   *   { action: 'subtask-completed' }
   *     Subtask was toggled on; XP and coins already awarded.
   *
   *   { action: 'subtask-toggled' }
   *     Subtask was toggled off; no XP change.
   *
   *   { action: 'missing-subtasks', completed, total }
   *     Main task blocked — not all steps are done yet.
   *
   *   { action: 'uncompleted' }
   *     Main task was toggled off (already-completed → uncomplete).
   *
   *   { action: 'needs-proof' }
   *     Ready to complete; caller should open proof capture then call
   *     finalizeCompletion() once the proof is saved.
   */
  const completeTask = useCallback((item, isSubtask = false, subIndex = null) => {
    // ── Subtask toggle ────────────────────────────────────────────────────────
    if (isSubtask && subIndex !== null) {
      const wasCompleted = isSubTaskCompleted(weekOffset, currentDayIndex, item.id, subIndex);
      toggleSubTask(weekOffset, currentDayIndex, item.id, subIndex);
      if (!wasCompleted) {
        gameification.completeTask(item, true);
        rewards.addCoins(3, 'subtask');
        return { action: 'subtask-completed' };
      }
      return { action: 'subtask-toggled' };
    }

    // ── Guard: all subtasks must be completed first ───────────────────────────
    const hasInstructions = Array.isArray(item.instructions) && item.instructions.length > 0;
    if (hasInstructions) {
      const completedCount = getCompletedCount(
        weekOffset, currentDayIndex, item.id, item.instructions.length
      );
      if (completedCount < item.instructions.length) {
        return { action: 'missing-subtasks', completed: completedCount, total: item.instructions.length };
      }
    }

    // ── Already completed — toggle off ────────────────────────────────────────
    if (isCompleted(weekOffset, currentDayIndex, item.id)) {
      toggleTask(weekOffset, currentDayIndex, item.id);
      return { action: 'uncompleted' };
    }

    // ── All conditions met — proof capture required before finalizing ─────────
    return { action: 'needs-proof' };
  }, [
    weekOffset, currentDayIndex,
    toggleTask, toggleSubTask,
    isCompleted, isSubTaskCompleted, getCompletedCount,
    gameification, rewards,
  ]);

  /**
   * finalizeCompletion — Called after proof is captured.
   * Marks the task as completed, awards XP/coins, updates vital-task streak.
   * Does NOT update agendaEvents, send Google notifications, or trigger UI —
   * those remain in the calling component (App.js).
   *
   * @param {object} item — task object
   * @returns {{ coinsEarned: number }}
   */
  const finalizeCompletion = useCallback((item) => {
    toggleTask(weekOffset, currentDayIndex, item.id);
    gameification.completeTask(item, false);

    const coinsEarned = item.category === 'vital' ? 15 : 10;
    rewards.addCoins(coinsEarned, item.category);

    if (item.category === 'vital') {
      gameification.updateStreak(true);
    }

    return { coinsEarned };
  }, [weekOffset, currentDayIndex, toggleTask, gameification, rewards]);

  // ─── Public interface ─────────────────────────────────────────────────────────
  return {
    // ── Task state & CRUD ──────────────────────────────────────────────────────
    tasks,
    addTask,          // addTask(taskData) — ids generated internally
    deleteTask,       // deleteTask(taskId)
    updateTask,       // updateTask(taskId, updates)
    getTasksForDate,  // getTasksForDate(dateStr) => Task[]
    clearAllTasks,
    tasksSyncing,
    tasksSyncError,

    // ── Completion state ───────────────────────────────────────────────────────
    toggleTask,
    toggleSubTask,
    isCompleted,
    isSubTaskCompleted,
    getCompletedCount,
    isTaskCompleted,  // (dayIndex, item) => boolean

    // ── Orchestrated completion (business logic, no UI side effects) ───────────
    completeTask,        // (item, isSubtask?, subIndex?) => ActionDescriptor
    finalizeCompletion,  // (item) => { coinsEarned }

    // ── Passthrough — consumed directly in JSX and other handlers ─────────────
    gameification,
    rewards,
  };
}

export default useTaskManager;
